import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, createCreditCard, updateCreditCard } from "@/lib/api";
import {
  creditCardCreateSchema,
  type CreditCard,
  type CreditCardCreate,
} from "@finance/shared";

interface CreditCardFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: CreditCard | null;
  onSaved: () => void;
}

interface FormState {
  name: string;
  issuer: string;
  current_balance_owed: string;
  credit_limit: string;
  due_date: string;
  minimum_payment: string;
}

const EMPTY: FormState = {
  name: "",
  issuer: "",
  current_balance_owed: "",
  credit_limit: "",
  due_date: "",
  minimum_payment: "",
};

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  return new Date(ts).toISOString().slice(0, 10);
}

function dateInputToIso(value: string): string | null {
  if (!value) return null;
  const ts = Date.parse(`${value}T00:00:00.000Z`);
  if (Number.isNaN(ts)) return null;
  return new Date(ts).toISOString();
}

function fromCard(card: CreditCard | null): FormState {
  if (!card) return EMPTY;
  return {
    name: card.name,
    issuer: card.issuer,
    current_balance_owed: String(card.current_balance_owed),
    credit_limit: String(card.credit_limit),
    due_date: isoToDateInput(card.due_date),
    minimum_payment:
      card.minimum_payment == null ? "" : String(card.minimum_payment),
  };
}

export function CreditCardFormDialog({
  open,
  onOpenChange,
  card,
  onSaved,
}: CreditCardFormDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(fromCard(card));
      setErrors({});
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [open, card]);

  const isEdit = card !== null;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    const fieldErrors: Record<string, string> = {};

    const balance = Number(form.current_balance_owed);
    if (form.current_balance_owed === "" || Number.isNaN(balance)) {
      fieldErrors.current_balance_owed = "Enter a valid number";
    }

    const limit = Number(form.credit_limit);
    if (form.credit_limit === "" || Number.isNaN(limit)) {
      fieldErrors.credit_limit = "Enter a valid number";
    }

    const minPayment =
      form.minimum_payment === "" ? null : Number(form.minimum_payment);
    if (minPayment !== null && Number.isNaN(minPayment)) {
      fieldErrors.minimum_payment = "Enter a valid number";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const payload: CreditCardCreate = {
      name: form.name.trim(),
      issuer: form.issuer.trim(),
      current_balance_owed: balance,
      credit_limit: limit,
      due_date: dateInputToIso(form.due_date),
      minimum_payment: minPayment,
    };

    const parsed = creditCardCreateSchema.safeParse(payload);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!flat[key]) flat[key] = issue.message;
      }
      setErrors(flat);
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      if (isEdit && card) {
        await updateCreditCard(card.id, parsed.data);
      } else {
        await createCreditCard(parsed.data);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Failed to save credit card");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit card" : "Add card"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update this manually tracked credit card."
              : "Track a credit card's balance and limit."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="card-name">Name</Label>
            <Input
              id="card-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Sapphire Preferred"
              autoFocus
              required
            />
            {errors.name ? (
              <p className="text-xs text-destructive">{errors.name}</p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="card-issuer">Issuer</Label>
            <Input
              id="card-issuer"
              value={form.issuer}
              onChange={(e) => update("issuer", e.target.value)}
              placeholder="Chase, Amex, ..."
              required
            />
            {errors.issuer ? (
              <p className="text-xs text-destructive">{errors.issuer}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="card-balance">Balance owed</Label>
              <Input
                id="card-balance"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.current_balance_owed}
                onChange={(e) => update("current_balance_owed", e.target.value)}
                placeholder="0.00"
                required
              />
              {errors.current_balance_owed ? (
                <p className="text-xs text-destructive">
                  {errors.current_balance_owed}
                </p>
              ) : null}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="card-limit">Credit limit</Label>
              <Input
                id="card-limit"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.credit_limit}
                onChange={(e) => update("credit_limit", e.target.value)}
                placeholder="0.00"
                required
              />
              {errors.credit_limit ? (
                <p className="text-xs text-destructive">
                  {errors.credit_limit}
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="card-due">Due date</Label>
              <Input
                id="card-due"
                type="date"
                value={form.due_date}
                onChange={(e) => update("due_date", e.target.value)}
              />
              {errors.due_date ? (
                <p className="text-xs text-destructive">{errors.due_date}</p>
              ) : null}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="card-min">Minimum payment</Label>
              <Input
                id="card-min"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={form.minimum_payment}
                onChange={(e) => update("minimum_payment", e.target.value)}
                placeholder="0.00"
              />
              {errors.minimum_payment ? (
                <p className="text-xs text-destructive">
                  {errors.minimum_payment}
                </p>
              ) : null}
            </div>
          </div>

          {submitError ? (
            <p
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              role="alert"
            >
              {submitError}
            </p>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : isEdit ? "Save changes" : "Add card"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
